/**
 * Key Risk Indicators (KRI) Page
 * GitHub Issue #127
 *
 * Manages KRIs with threshold monitoring, value tracking, and risk linking.
 */

import { useState, useEffect, useCallback } from 'react'
import { api, KRI, KRIValue, KRIAlert, KRIRiskLink, CreateKRIInput, UpdateKRIInput, RiskCategory } from '../lib/api'
import { useToast } from '../components/Toast'
import { useConfirm } from '../components/ConfirmModal'
import {
  Activity,
  Plus,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Edit2,
  Trash2,
  X,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  Bell,
  BellOff,
  Link2,
  Unlink,
  User,
  Clock,
  BarChart3,
  PieChart,
  Info,
  CheckCircle2,
  XCircle,
  AlertCircle,
  HelpCircle,
} from 'lucide-react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  AreaChart,
} from 'recharts'

// Status badge colors
const statusColors = {
  green: 'bg-green-100 text-green-800 border-green-200',
  yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  red: 'bg-red-100 text-red-800 border-red-200',
}

const statusIcons = {
  green: CheckCircle2,
  yellow: AlertCircle,
  red: XCircle,
}

// Dynamic category colors based on DB category colors
const getCategoryStyle = (color?: string): string => {
  if (!color) return 'bg-gray-50 text-gray-700 border-gray-200'
  // Convert hex color to Tailwind-like classes with opacity
  return `border-opacity-30`
}

const metricTypeLabels: Record<string, string> = {
  number: 'Number',
  percentage: 'Percentage',
  currency: 'Currency',
  count: 'Count',
  days: 'Days',
  ratio: 'Ratio',
}

const directionLabels: Record<string, string> = {
  lower_is_better: 'Lower is Better',
  higher_is_better: 'Higher is Better',
}

interface UserOption {
  id: string
  username: string
}

export default function KRIs() {
  // State
  const [kris, setKris] = useState<KRI[]>([])
  const [alerts, setAlerts] = useState<KRIAlert[]>([])
  const [riskCategories, setRiskCategories] = useState<RiskCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [users, setUsers] = useState<UserOption[]>([])

  // Dashboard summary
  const [summary, setSummary] = useState({
    total: 0,
    green: 0,
    yellow: 0,
    red: 0,
    no_data: 0,
  })

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showRecordValueModal, setShowRecordValueModal] = useState(false)
  const [showAlertsPanel, setShowAlertsPanel] = useState(false)
  const [showHelpModal, setShowHelpModal] = useState(false)
  const [selectedKRI, setSelectedKRI] = useState<KRI | null>(null)
  const [kriDetail, setKriDetail] = useState<(KRI & { linked_risks: KRIRiskLink[]; recent_values: KRIValue[] }) | null>(null)

  // Form state
  const [formData, setFormData] = useState<CreateKRIInput>({
    name: '',
    description: '',
    metric_type: 'number',
    unit: '',
    green_threshold: 0,
    yellow_threshold: 0,
    red_threshold: undefined,
    direction: 'lower_is_better',
    category_id: '',
    owner_id: '',
    alert_on_breach: true,
  })

  const [recordValueData, setRecordValueData] = useState({
    value: 0,
    notes: '',
  })
  const [seeding, setSeeding] = useState(false)

  const toast = useToast()
  const confirmDialog = useConfirm()

  // Fetch data
  const fetchKRIs = useCallback(async () => {
    try {
      const response = await api.getKRIDashboard()
      if (response.success) {
        setKris(response.dashboard.kris)
        setSummary(response.dashboard.summary)
      }
    } catch (error) {
      toast.error('Failed to fetch KRIs')
    }
  }, [toast])

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await api.getKRIAlerts(false, 20)
      if (response.success) {
        setAlerts(response.alerts)
      }
    } catch (error) {
      console.error('Failed to fetch alerts:', error)
    }
  }, [])

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.getUsers()
      if (response.success) {
        setUsers(response.users.map((u: any) => ({ id: u.id, username: u.username })))
      }
    } catch (error) {
      console.error('Failed to fetch users:', error)
    }
  }, [])

  const fetchRiskCategories = useCallback(async () => {
    try {
      const response = await api.getRiskCategories({ flat: true })
      if (response.success && response.categories) {
        setRiskCategories(response.categories)
      }
    } catch (error) {
      console.error('Failed to fetch risk categories:', error)
    }
  }, [])

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await Promise.all([fetchKRIs(), fetchAlerts(), fetchUsers(), fetchRiskCategories()])
      setLoading(false)
    }
    loadData()
  }, [fetchKRIs, fetchAlerts, fetchUsers, fetchRiskCategories])

  // Filter KRIs
  const filteredKRIs = kris.filter((kri) => {
    if (searchTerm && !kri.name.toLowerCase().includes(searchTerm.toLowerCase())) return false
    if (categoryFilter && kri.category !== categoryFilter) return false
    if (statusFilter && kri.current_status !== statusFilter) return false
    return true
  })

  // Get unique categories
  const categories = [...new Set(kris.map((k) => k.category).filter(Boolean))]

  // Create KRI
  const handleCreate = async () => {
    try {
      const response = await api.createKRI(formData)
      if (response.success) {
        toast.success('KRI created successfully')
        setShowCreateModal(false)
        resetForm()
        fetchKRIs()
      }
    } catch (error) {
      toast.error('Failed to create KRI')
    }
  }

  // Update KRI
  const handleUpdate = async () => {
    if (!selectedKRI) return
    try {
      const response = await api.updateKRI(selectedKRI.id, formData as UpdateKRIInput)
      if (response.success) {
        toast.success('KRI updated successfully')
        setShowEditModal(false)
        setSelectedKRI(null)
        resetForm()
        fetchKRIs()
      }
    } catch (error) {
      toast.error('Failed to update KRI')
    }
  }

  // Delete KRI
  const handleDelete = async (kri: KRI) => {
    const confirmed = await confirmDialog({
      title: 'Delete KRI',
      message: `Are you sure you want to delete "${kri.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      type: 'danger',
    })

    if (confirmed) {
      try {
        const response = await api.deleteKRI(kri.id)
        if (response.success) {
          toast.success('KRI deleted successfully')
          fetchKRIs()
        }
      } catch (error) {
        toast.error('Failed to delete KRI')
      }
    }
  }

  // Seed example KRIs
  const handleSeedKRIs = async () => {
    setSeeding(true)
    try {
      const response = await api.seedKRIs()
      if (response.success) {
        toast.success(response.message || 'Example KRIs loaded successfully')
        fetchKRIs()
      } else {
        toast.error(response.error || 'Failed to seed KRIs')
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to seed KRIs')
    } finally {
      setSeeding(false)
    }
  }

  // Record value
  const handleRecordValue = async () => {
    if (!selectedKRI) return
    try {
      const response = await api.recordKRIValue(selectedKRI.id, recordValueData)
      if (response.success) {
        toast.success('Value recorded successfully')
        if (response.status_changed) {
          toast.warning(`Status changed from ${response.previous_status} to ${response.value.status}`)
          fetchAlerts()
        }
        setShowRecordValueModal(false)
        setRecordValueData({ value: 0, notes: '' })
        fetchKRIs()
        if (kriDetail && kriDetail.id === selectedKRI.id) {
          fetchKRIDetail(selectedKRI.id)
        }
      }
    } catch (error) {
      toast.error('Failed to record value')
    }
  }

  // Fetch KRI detail
  const fetchKRIDetail = async (id: string) => {
    try {
      const response = await api.getKRI(id)
      if (response.success) {
        setKriDetail(response.kri)
      }
    } catch (error) {
      toast.error('Failed to fetch KRI details')
    }
  }

  // Open detail modal
  const openDetailModal = async (kri: KRI) => {
    setSelectedKRI(kri)
    setShowDetailModal(true)
    await fetchKRIDetail(kri.id)
  }

  // Open edit modal
  const openEditModal = (kri: KRI) => {
    setSelectedKRI(kri)
    setFormData({
      name: kri.name,
      description: kri.description || '',
      metric_type: kri.metric_type,
      unit: kri.unit || '',
      green_threshold: kri.green_threshold,
      yellow_threshold: kri.yellow_threshold,
      red_threshold: kri.red_threshold,
      direction: kri.direction,
      category_id: (kri as any).category_id || '',
      owner_id: kri.owner_id || '',
      alert_on_breach: kri.alert_on_breach === 1,
    })
    setShowEditModal(true)
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      metric_type: 'number',
      unit: '',
      green_threshold: 0,
      yellow_threshold: 0,
      red_threshold: undefined,
      direction: 'lower_is_better',
      category_id: '',
      owner_id: '',
      alert_on_breach: true,
    })
  }

  // Acknowledge alert
  const handleAcknowledgeAlert = async (alertId: string) => {
    try {
      await api.acknowledgeKRIAlert(alertId)
      toast.success('Alert acknowledged')
      fetchAlerts()
    } catch (error) {
      toast.error('Failed to acknowledge alert')
    }
  }

  // Dismiss alert
  const handleDismissAlert = async (alertId: string) => {
    try {
      await api.dismissKRIAlert(alertId)
      toast.success('Alert dismissed')
      fetchAlerts()
    } catch (error) {
      toast.error('Failed to dismiss alert')
    }
  }

  // Format value based on metric type
  const formatValue = (value: number | undefined, metricType: string, unit?: string) => {
    if (value === undefined || value === null) return 'N/A'
    switch (metricType) {
      case 'percentage':
        return `${value}%`
      case 'currency':
        return `${unit || '$'}${value.toLocaleString()}`
      case 'days':
        return `${value} days`
      default:
        return `${value}${unit || ''}`
    }
  }

  // Get trend icon
  const getTrendIcon = (currentValue?: number, recentValues?: KRIValue[]) => {
    if (!recentValues || recentValues.length < 2 || currentValue === undefined) return Minus
    const previousValue = recentValues[1]?.value
    if (previousValue === undefined) return Minus
    if (currentValue > previousValue) return TrendingUp
    if (currentValue < previousValue) return TrendingDown
    return Minus
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">Key Risk Indicators</h1>
            <button
              onClick={() => setShowHelpModal(true)}
              className="text-gray-400 hover:text-primary-600 transition-colors"
              title="What are KRIs?"
            >
              <HelpCircle className="h-5 w-5" />
            </button>
          </div>
          <p className="text-gray-500 mt-1">Monitor and track risk metrics with threshold-based alerts</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAlertsPanel(!showAlertsPanel)}
            className={`relative btn ${alerts.length > 0 ? 'btn-warning' : 'btn-secondary'}`}
          >
            <Bell className="h-4 w-4 mr-2" />
            Alerts
            {alerts.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {alerts.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            New KRI
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-500 text-sm">Total KRIs</span>
            <Activity className="h-5 w-5 text-gray-400" />
          </div>
          <p className="text-2xl font-bold mt-2">{summary.total}</p>
        </div>
        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <div className="flex items-center justify-between">
            <span className="text-green-700 text-sm">On Target</span>
            <CheckCircle2 className="h-5 w-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-green-700 mt-2">{summary.green}</p>
        </div>
        <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
          <div className="flex items-center justify-between">
            <span className="text-yellow-700 text-sm">Warning</span>
            <AlertCircle className="h-5 w-5 text-yellow-500" />
          </div>
          <p className="text-2xl font-bold text-yellow-700 mt-2">{summary.yellow}</p>
        </div>
        <div className="bg-red-50 rounded-lg border border-red-200 p-4">
          <div className="flex items-center justify-between">
            <span className="text-red-700 text-sm">Critical</span>
            <XCircle className="h-5 w-5 text-red-500" />
          </div>
          <p className="text-2xl font-bold text-red-700 mt-2">{summary.red}</p>
        </div>
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-700 text-sm">No Data</span>
            <Minus className="h-5 w-5 text-gray-400" />
          </div>
          <p className="text-2xl font-bold text-gray-700 mt-2">{summary.no_data}</p>
        </div>
      </div>

      {/* Alerts Panel */}
      {showAlertsPanel && alerts.length > 0 && (
        <div className="bg-white rounded-lg border shadow-sm">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Unacknowledged Alerts</h3>
            <button onClick={() => setShowAlertsPanel(false)} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="divide-y max-h-64 overflow-y-auto">
            {alerts.map((alert) => (
              <div key={alert.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${alert.new_status === 'red' ? 'bg-red-100' : 'bg-yellow-100'}`}>
                    <AlertTriangle className={`h-4 w-4 ${alert.new_status === 'red' ? 'text-red-600' : 'text-yellow-600'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{alert.kri_name}</p>
                    <p className="text-sm text-gray-500">{alert.message}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(alert.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleAcknowledgeAlert(alert.id)}
                    className="btn btn-sm btn-secondary"
                  >
                    Acknowledge
                  </button>
                  <button
                    onClick={() => handleDismissAlert(alert.id)}
                    className="btn btn-sm text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search KRIs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn btn-secondary"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {showFilters ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="input w-full"
              >
                <option value="">All Categories</option>
                {riskCategories.map((cat) => (
                  <option key={cat.id} value={cat.name.toLowerCase()}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input w-full"
              >
                <option value="">All Statuses</option>
                <option value="green">On Target (Green)</option>
                <option value="yellow">Warning (Yellow)</option>
                <option value="red">Critical (Red)</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setCategoryFilter('')
                  setStatusFilter('')
                  setSearchTerm('')
                }}
                className="btn btn-secondary"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* KRI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredKRIs.map((kri) => {
          const StatusIcon = kri.current_status ? statusIcons[kri.current_status] : Minus
          const TrendIcon = getTrendIcon(kri.current_value, [])

          return (
            <div
              key={kri.id}
              className="bg-white rounded-lg border hover:border-primary-300 hover:shadow-md transition-all cursor-pointer"
              onClick={() => openDetailModal(kri)}
            >
              <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 line-clamp-1">{kri.name}</h3>
                    {kri.category && (
                      <span
                        className="inline-block mt-1 px-2 py-0.5 text-xs rounded border"
                        style={{
                          backgroundColor: `${riskCategories.find(c => c.name.toLowerCase() === kri.category)?.color || '#6B7280'}20`,
                          color: riskCategories.find(c => c.name.toLowerCase() === kri.category)?.color || '#6B7280',
                          borderColor: `${riskCategories.find(c => c.name.toLowerCase() === kri.category)?.color || '#6B7280'}40`,
                        }}
                      >
                        {kri.category}
                      </span>
                    )}
                  </div>
                  {kri.current_status ? (
                    <div className={`p-2 rounded-lg ${kri.current_status === 'green' ? 'bg-green-100' : kri.current_status === 'yellow' ? 'bg-yellow-100' : 'bg-red-100'}`}>
                      <StatusIcon className={`h-5 w-5 ${kri.current_status === 'green' ? 'text-green-600' : kri.current_status === 'yellow' ? 'text-yellow-600' : 'text-red-600'}`} />
                    </div>
                  ) : (
                    <div className="p-2 rounded-lg bg-gray-100">
                      <Minus className="h-5 w-5 text-gray-400" />
                    </div>
                  )}
                </div>

                <div className="flex items-baseline gap-2 mb-3">
                  <span className="text-3xl font-bold text-gray-900">
                    {formatValue(kri.current_value, kri.metric_type, kri.unit)}
                  </span>
                  <TrendIcon className={`h-4 w-4 ${TrendIcon === TrendingUp ? 'text-red-500' : TrendIcon === TrendingDown ? 'text-green-500' : 'text-gray-400'}`} />
                </div>

                <div className="text-xs text-gray-500 space-y-1">
                  <div className="flex justify-between">
                    <span>Green threshold:</span>
                    <span className="text-green-600 font-medium">{kri.direction === 'lower_is_better' ? '<=' : '>='} {formatValue(kri.green_threshold, kri.metric_type, kri.unit)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Yellow threshold:</span>
                    <span className="text-yellow-600 font-medium">{kri.direction === 'lower_is_better' ? '<=' : '>='} {formatValue(kri.yellow_threshold, kri.metric_type, kri.unit)}</span>
                  </div>
                </div>

                {kri.last_recorded_at && (
                  <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs text-gray-400">
                    <span>Last updated: {new Date(kri.last_recorded_at).toLocaleDateString()}</span>
                    {kri.linked_risks_count !== undefined && kri.linked_risks_count > 0 && (
                      <span className="flex items-center gap-1">
                        <Link2 className="h-3 w-3" />
                        {kri.linked_risks_count} risks
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="border-t p-2 flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => {
                    setSelectedKRI(kri)
                    setShowRecordValueModal(true)
                  }}
                  className="btn btn-sm btn-primary"
                >
                  Record Value
                </button>
                <button
                  onClick={() => openEditModal(kri)}
                  className="btn btn-sm btn-secondary"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(kri)}
                  className="btn btn-sm text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {filteredKRIs.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border">
          <Activity className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No KRIs found</h3>
          {kris.length === 0 ? (
            <>
              <p className="text-gray-600 mb-2 max-w-lg mx-auto">
                Key Risk Indicators are measurable metrics that provide early warning signals about increasing risk exposure.
              </p>
              <p className="text-gray-500 mb-6 max-w-lg mx-auto text-sm">
                Track metrics like incident rates, compliance percentages, and system uptime with color-coded thresholds (green/yellow/red) to monitor your risk posture.
                <button
                  onClick={() => setShowHelpModal(true)}
                  className="text-primary-600 hover:text-primary-700 ml-1 underline"
                >
                  Learn more
                </button>
              </p>
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={handleSeedKRIs}
                  disabled={seeding}
                  className="btn btn-secondary"
                >
                  {seeding ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Loading...
                    </>
                  ) : (
                    <>
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Load Example KRIs
                    </>
                  )}
                </button>
                <span className="text-gray-400">or</span>
                <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Create KRI
                </button>
              </div>
            </>
          ) : (
            <p className="text-gray-500 mb-4">No KRIs match your search criteria.</p>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => e.target === e.currentTarget && (showCreateModal ? setShowCreateModal(false) : setShowEditModal(false))}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {showCreateModal ? 'Create New KRI' : 'Edit KRI'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setShowEditModal(false)
                  setSelectedKRI(null)
                  resetForm()
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input w-full"
                    placeholder="e.g., Security Incident Rate"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input w-full"
                    rows={2}
                    placeholder="Describe what this KRI measures..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Metric Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.metric_type}
                      onChange={(e) => setFormData({ ...formData, metric_type: e.target.value as any })}
                      className="input w-full"
                    >
                      {Object.entries(metricTypeLabels).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit (optional)</label>
                    <input
                      type="text"
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      className="input w-full"
                      placeholder="e.g., %, $, incidents"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Direction <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.direction}
                    onChange={(e) => setFormData({ ...formData, direction: e.target.value as any })}
                    className="input w-full"
                  >
                    {Object.entries(directionLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.direction === 'lower_is_better'
                      ? 'Lower values are better (e.g., incident count, error rate)'
                      : 'Higher values are better (e.g., uptime, satisfaction score)'}
                  </p>
                </div>
              </div>

              {/* Thresholds */}
              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-900 mb-4">Threshold Configuration</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-green-700 mb-1">
                      Green Threshold <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.green_threshold}
                      onChange={(e) => setFormData({ ...formData, green_threshold: parseFloat(e.target.value) || 0 })}
                      className="input w-full border-green-300 focus:border-green-500 focus:ring-green-500"
                      step="any"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-yellow-700 mb-1">
                      Yellow Threshold <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.yellow_threshold}
                      onChange={(e) => setFormData({ ...formData, yellow_threshold: parseFloat(e.target.value) || 0 })}
                      className="input w-full border-yellow-300 focus:border-yellow-500 focus:ring-yellow-500"
                      step="any"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-red-700 mb-1">
                      Red Threshold
                    </label>
                    <input
                      type="number"
                      value={formData.red_threshold || ''}
                      onChange={(e) => setFormData({ ...formData, red_threshold: e.target.value ? parseFloat(e.target.value) : undefined })}
                      className="input w-full border-red-300 focus:border-red-500 focus:ring-red-500"
                      step="any"
                      placeholder="Optional"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {formData.direction === 'lower_is_better'
                    ? 'Values <= green are good, values <= yellow are warnings, above yellow are critical'
                    : 'Values >= green are good, values >= yellow are warnings, below yellow are critical'}
                </p>
              </div>

              {/* Additional Settings */}
              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-900 mb-4">Additional Settings</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={formData.category_id}
                      onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                      className="input w-full"
                    >
                      <option value="">Select Category</option>
                      {riskCategories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
                    <select
                      value={formData.owner_id}
                      onChange={(e) => setFormData({ ...formData, owner_id: e.target.value })}
                      className="input w-full"
                    >
                      <option value="">Select Owner</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>{user.username}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.alert_on_breach}
                      onChange={(e) => setFormData({ ...formData, alert_on_breach: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">
                      Alert when thresholds are breached
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setShowEditModal(false)
                  setSelectedKRI(null)
                  resetForm()
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={showCreateModal ? handleCreate : handleUpdate}
                className="btn btn-primary"
                disabled={!formData.name || formData.green_threshold === undefined || formData.yellow_threshold === undefined}
              >
                {showCreateModal ? 'Create KRI' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Record Value Modal */}
      {showRecordValueModal && selectedKRI && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => e.target === e.currentTarget && setShowRecordValueModal(false)}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Record Value</h2>
                <p className="text-sm text-gray-500">{selectedKRI.name}</p>
              </div>
              <button
                onClick={() => {
                  setShowRecordValueModal(false)
                  setRecordValueData({ value: 0, notes: '' })
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Value <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={recordValueData.value}
                    onChange={(e) => setRecordValueData({ ...recordValueData, value: parseFloat(e.target.value) || 0 })}
                    className="input flex-1"
                    step="any"
                  />
                  {selectedKRI.unit && (
                    <span className="text-gray-500">{selectedKRI.unit}</span>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={recordValueData.notes}
                  onChange={(e) => setRecordValueData({ ...recordValueData, notes: e.target.value })}
                  className="input w-full"
                  rows={2}
                  placeholder="Optional notes about this measurement..."
                />
              </div>

              {/* Threshold reference */}
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="font-medium text-gray-700 mb-2">Threshold Reference:</p>
                <div className="space-y-1 text-gray-600">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-green-500"></span>
                    Green: {selectedKRI.direction === 'lower_is_better' ? '<=' : '>='} {formatValue(selectedKRI.green_threshold, selectedKRI.metric_type, selectedKRI.unit)}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                    Yellow: {selectedKRI.direction === 'lower_is_better' ? '<=' : '>='} {formatValue(selectedKRI.yellow_threshold, selectedKRI.metric_type, selectedKRI.unit)}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-red-500"></span>
                    Red: {selectedKRI.direction === 'lower_is_better' ? '>' : '<'} {formatValue(selectedKRI.yellow_threshold, selectedKRI.metric_type, selectedKRI.unit)}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowRecordValueModal(false)
                  setRecordValueData({ value: 0, notes: '' })
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleRecordValue}
                className="btn btn-primary"
              >
                Record Value
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedKRI && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => e.target === e.currentTarget && setShowDetailModal(false)}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{selectedKRI.name}</h2>
                <p className="text-sm text-gray-500">{selectedKRI.description}</p>
              </div>
              <button
                onClick={() => {
                  setShowDetailModal(false)
                  setKriDetail(null)
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {kriDetail ? (
              <div className="p-6 space-y-6">
                {/* Current Status */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500 mb-1">Current Value</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {formatValue(kriDetail.current_value, kriDetail.metric_type, kriDetail.unit)}
                    </p>
                  </div>
                  <div className={`rounded-lg p-4 ${kriDetail.current_status ? statusColors[kriDetail.current_status] : 'bg-gray-50'}`}>
                    <p className="text-sm opacity-75 mb-1">Status</p>
                    <p className="text-xl font-bold capitalize">
                      {kriDetail.current_status || 'No Data'}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500 mb-1">Last Updated</p>
                    <p className="text-xl font-bold text-gray-900">
                      {kriDetail.last_recorded_at
                        ? new Date(kriDetail.last_recorded_at).toLocaleDateString()
                        : 'Never'}
                    </p>
                  </div>
                </div>

                {/* Trend Chart */}
                {kriDetail.recent_values && kriDetail.recent_values.length > 1 && (
                  <div className="border rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-4">Historical Trend</h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={[...kriDetail.recent_values].reverse().map((v) => ({
                            date: new Date(v.recorded_at).toLocaleDateString(),
                            value: v.value,
                            status: v.status,
                          }))}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <ReferenceLine
                            y={kriDetail.green_threshold}
                            stroke="#22c55e"
                            strokeDasharray="5 5"
                            label="Green"
                          />
                          <ReferenceLine
                            y={kriDetail.yellow_threshold}
                            stroke="#eab308"
                            strokeDasharray="5 5"
                            label="Yellow"
                          />
                          <Area
                            type="monotone"
                            dataKey="value"
                            stroke="#6366f1"
                            fill="#6366f1"
                            fillOpacity={0.2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Value History */}
                <div className="border rounded-lg">
                  <div className="p-4 border-b flex items-center justify-between">
                    <h3 className="font-medium text-gray-900">Value History</h3>
                    <button
                      onClick={() => {
                        setShowRecordValueModal(true)
                      }}
                      className="btn btn-sm btn-primary"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Record Value
                    </button>
                  </div>
                  <div className="divide-y max-h-64 overflow-y-auto">
                    {kriDetail.recent_values && kriDetail.recent_values.length > 0 ? (
                      kriDetail.recent_values.map((value) => (
                        <div key={value.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${value.status === 'green' ? 'bg-green-500' : value.status === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                            <div>
                              <p className="font-medium text-gray-900">
                                {formatValue(value.value, kriDetail.metric_type, kriDetail.unit)}
                              </p>
                              {value.notes && (
                                <p className="text-sm text-gray-500">{value.notes}</p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500">
                              {new Date(value.recorded_at).toLocaleString()}
                            </p>
                            {value.recorded_by_username && (
                              <p className="text-xs text-gray-400">by {value.recorded_by_username}</p>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500">
                        No values recorded yet
                      </div>
                    )}
                  </div>
                </div>

                {/* Linked Risks */}
                <div className="border rounded-lg">
                  <div className="p-4 border-b">
                    <h3 className="font-medium text-gray-900">Linked Risks</h3>
                  </div>
                  <div className="divide-y">
                    {kriDetail.linked_risks && kriDetail.linked_risks.length > 0 ? (
                      kriDetail.linked_risks.map((link) => (
                        <div key={link.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                          <div>
                            <p className="font-medium text-gray-900">{link.risk_title}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500">{link.relationship_type}</span>
                              {link.risk_score !== undefined && (
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  link.risk_score >= 20 ? 'bg-red-100 text-red-700' :
                                  link.risk_score >= 12 ? 'bg-orange-100 text-orange-700' :
                                  link.risk_score >= 6 ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-green-100 text-green-700'
                                }`}>
                                  Score: {link.risk_score}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ${
                            link.risk_status === 'open' ? 'bg-red-100 text-red-700' :
                            link.risk_status === 'mitigating' ? 'bg-yellow-100 text-yellow-700' :
                            link.risk_status === 'mitigated' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {link.risk_status}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500">
                        No risks linked to this KRI
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Help Modal - Issue #272 */}
      {showHelpModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => e.target === e.currentTarget && setShowHelpModal(false)}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <HelpCircle className="h-6 w-6 text-primary-600" />
                Understanding Key Risk Indicators
              </h2>
              <button
                onClick={() => setShowHelpModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* What are KRIs */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">What are Key Risk Indicators?</h3>
                <p className="text-gray-600">
                  KRIs are measurable metrics that provide early warning signals about increasing risk exposure.
                  They help you proactively monitor risk before issues become incidents, enabling timely
                  intervention and risk mitigation.
                </p>
              </div>

              {/* How Thresholds Work */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">How Thresholds Work</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Green
                    </span>
                    <span className="text-gray-600">On target, healthy - no action needed</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                      <AlertCircle className="h-4 w-4 mr-1" /> Yellow
                    </span>
                    <span className="text-gray-600">Warning zone - needs attention soon</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                      <XCircle className="h-4 w-4 mr-1" /> Red
                    </span>
                    <span className="text-gray-600">Critical - requires immediate action</span>
                  </div>
                </div>
              </div>

              {/* Direction Types */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Direction Types</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingDown className="h-5 w-5 text-blue-600" />
                      <span className="font-medium text-gray-900">Lower is Better</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">For metrics where you want low values:</p>
                    <ul className="text-sm text-gray-500 list-disc list-inside">
                      <li>Incident count</li>
                      <li>Error rate</li>
                      <li>Response time</li>
                      <li>Open vulnerabilities</li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                      <span className="font-medium text-gray-900">Higher is Better</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">For metrics where you want high values:</p>
                    <ul className="text-sm text-gray-500 list-disc list-inside">
                      <li>System uptime %</li>
                      <li>Training completion %</li>
                      <li>Patch compliance %</li>
                      <li>Control effectiveness</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Linking to Risks */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Linking to Risks</h3>
                <p className="text-gray-600">
                  KRIs can be linked to risks in your risk register. When a KRI breaches a threshold,
                  it provides evidence that linked risks may be materializing. This creates a direct
                  connection between your metrics and risk management activities.
                </p>
              </div>

              {/* Example Use Cases */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Example Use Cases</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="border rounded-lg p-3">
                    <span className="text-sm font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded">Security</span>
                    <p className="text-sm text-gray-600 mt-2">Track incident rate, mean time to detect, unpatched critical systems</p>
                  </div>
                  <div className="border rounded-lg p-3">
                    <span className="text-sm font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded">Compliance</span>
                    <p className="text-sm text-gray-600 mt-2">Track policy acknowledgment rate, audit findings, training completion</p>
                  </div>
                  <div className="border rounded-lg p-3">
                    <span className="text-sm font-medium text-purple-700 bg-purple-50 px-2 py-0.5 rounded">Operational</span>
                    <p className="text-sm text-gray-600 mt-2">Track system uptime, change failure rate, ticket backlog age</p>
                  </div>
                  <div className="border rounded-lg p-3">
                    <span className="text-sm font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded">Financial</span>
                    <p className="text-sm text-gray-600 mt-2">Track budget variance, cost per incident, vendor spend</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50">
              <button
                onClick={() => setShowHelpModal(false)}
                className="btn btn-primary w-full"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
